import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './Sidebar.css';

const Sidebar = () => {
  const { user, getAuthHeaders } = useAuth();

  // Feature gating: the "Push Notifications" item is hidden when the tenant has
  // features.push_notifications disabled. Resolved from GET /api/v1/config
  // (admin variant — tenant from JWT). Default true (show) so a slow/failed
  // fetch never hides a legitimately-enabled item; the server enforces the flag
  // regardless (notificationController.requirePushEnabled).
  const [pushEnabled, setPushEnabled] = useState(true);

  // Feature gating: the "Stampbook" section is hidden when the tenant has
  // features.stampbook disabled (VirQ: false). Same GET /api/v1/config source as
  // pushEnabled. Default true (show) so a slow/failed fetch never hides a
  // legitimately-enabled section; only an explicit `false` hides it. The
  // underlying /stampbook-* routes stay mounted in App.js — this is nav-only.
  const [stampbookEnabled, setStampbookEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/v1/config', { headers: { ...getAuthHeaders() } });
        if (!res.ok) return;
        const body = await res.json();
        const features = body?.data?.features || {};
        if (!cancelled && typeof features.push_notifications === 'boolean') setPushEnabled(features.push_notifications);
        if (!cancelled && typeof features.stampbook === 'boolean') setStampbookEnabled(features.stampbook);
      } catch (_err) {
        // Network/parse error — leave defaults (show); server still enforces.
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeaders]);

  const contentItems = [
    { path: '/', name: 'Dashboard', icon: '📊', end: true },
    { path: '/attractions', name: 'Attractions', icon: '🎯' },
    { path: '/events', name: 'Events', icon: '📅' },
    { path: '/banners', name: 'Banner Management', icon: '🏷️' },
    { path: '/home-cards', name: 'Home Card Management', icon: '🏠' },
    { path: '/feedback-manager', name: 'Feedback Manager', icon: '💬' },
    { path: '/faq-manager', name: 'FAQ Manager', icon: '❓' },
    ...(pushEnabled ? [{ path: '/notification-manager', name: 'Push Notifications', icon: '🔔' }] : []),
  ];

  // QR section hidden for VirQ — feature not in use. No tenant feature flag
  // exists for QR (unlike stampbook/push), so the nav is commented out rather
  // than gated. Reversible: uncomment this block and its render below to restore.
  // The /qr-generator and /qr-analytics routes remain mounted in App.js.
  // const qrItems = [
  //   { path: '/qr-generator', name: 'QR Code Generator', icon: '📱' },
  //   { path: '/qr-analytics', name: 'QR Analytics', icon: '📊' },
  // ];

  const stampbookItems = [
    { path: '/stampbook-analytics', name: 'Stampbook Analytics', icon: '🏆' },
    { path: '/stampbook-configuration', name: 'Stampbook Configuration', icon: '⚙️' },
  ];

  const itineraryItems = [
    { path: '/itinerary/dashboard', name: 'Dashboard', icon: '📊' },
    { path: '/itinerary/history', name: 'History', icon: '📋' },
    // Hidden for VirQ (nav-only, reversible — uncomment to restore):
    //   • Presets — feature not built out yet (empty).
    //   • Testing Console — internal debug tool; still surfaces Melaka
    //     districts/interests, must not be visible to a tenant admin in prod.
    // The /itinerary/presets and /itinerary/testing routes stay mounted in
    // App.js — reachable directly, just not surfaced in the sidebar.
    // { path: '/itinerary/presets', name: 'Presets', icon: '🗂️' },
    // { path: '/itinerary/testing', name: 'Testing Console', icon: '🧪' },
  ];

  const adminItems = [
    ...(user?.username === 'admin' ? [{
      path: '/admin/users',
      name: 'User Management',
      icon: '👥',
      adminOnly: true
    }] : []),
    { path: '/user/profile', name: 'App User Management', icon: '👤' },
    { path: '/activity-log', name: 'Activity Log', icon: '📝' },
  ];

  const renderItems = (items) =>
    items.map((item) => (
      <li key={item.path} className={item.adminOnly ? 'admin-only' : ''}>
        <NavLink
          to={item.path}
          className={({ isActive }) =>
            `nav-link ${isActive ? 'active' : ''} ${item.adminOnly ? 'admin-feature' : ''}`
          }
          end={!!item.end}
        >
          <span className="nav-icon">{item.icon}</span>
          <span className="nav-text">{item.name}</span>
          {item.adminOnly && (
            <span className="admin-badge" title="Super Admin Only">SA</span>
          )}
        </NavLink>
      </li>
    ));

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <img
          src={`${process.env.PUBLIC_URL}/future-objects-logo.png`}
          alt="Future Objects"
          className="sidebar-logo"
          style={{ width: '100%', maxWidth: 160, height: 'auto', display: 'block', margin: '0 auto' }}
        />
      </div>

      <nav className="sidebar-nav">
        <ul>
          <li className="sidebar-section-label">Content</li>
          {renderItems(contentItems)}

          {/* QR section hidden for VirQ (feature not in use; no tenant flag).
              Reversible — uncomment with the qrItems declaration above.
          <li className="sidebar-section-label">QR</li>
          {renderItems(qrItems)}
          */}

          {stampbookEnabled && (
            <>
              <li className="sidebar-section-label">Stampbook</li>
              {renderItems(stampbookItems)}
            </>
          )}

          <li className="sidebar-section-label">Itinerary Planner</li>
          {renderItems(itineraryItems)}

          <li className="sidebar-section-label">Admin</li>
          {renderItems(adminItems)}

          <li className="sidebar-section-label">AI Assistant</li>
          <li>
            <a
              href="/admin/vmai"
              className="nav-link"
            >
              <span className="nav-icon">🤖</span>
              <span className="nav-text">Imatra AI</span>
            </a>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default Sidebar;
